import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar, Button, Drawer, Empty, Input, Select, Space, Typography, message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { Table, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@app/components/common/Table/Table';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import {
  PageTitle, ResultCount, Toolbar, ToolbarActions,
} from '@app/components/common/PageToolbar/PageToolbar';
import { getFeedback, getFeedbacks, postFeedbackResponse } from '@app/api/admin.api';
import { Feedback, FeedbackQuery } from '@app/pages/Feedback/types';
import { getProfilePhoto } from '@app/utils/utils';
import {
  CellMain, CellSub, DateCell, Muted, Pill,
} from '@app/pages/ShopifyOrders/ShopifyOrders.styles';

const { Text, Title } = Typography;

const TONE_REPLIED = '#62d7c6';
const TONE_UNREPLIED = '#ffaa00';

type Filters = { replied: '' | 'yes' | 'no'; brand: string };
const EMPTY_FILTERS: Filters = { replied: '', brand: '' };

const fmtDate = (iso: string | null) => (iso ? dayjs(iso).format('MMM D, YYYY') : '—');

// The app appends device metadata after a blank line; the preview is just the
// customer's own words.
const preview = (body: string) => (body || '').split('\n\n')[0].replace(/\s+/g, ' ').trim();

const noWrapTitle = (label: string) => <span style={{ whiteSpace: 'nowrap' }}>{label}</span>;

const FeedbackPage: React.FC = () => {
  const [rows, setRows] = useState<Feedback[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  const [open, setOpen] = useState<Feedback | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  // The open feedback lives in the address (?id=123) so a link -- e.g. from
  // the Slack post -- opens it directly, and Back closes the drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = Number(searchParams.get('id')) || null;

  const fetchRows = useCallback((nextPage: number, term: string, f: Filters, size: number) => {
    setLoading(true);
    const params: FeedbackQuery = {
      page: nextPage,
      page_size: size,
      search: term.trim() || undefined,
      replied: f.replied || undefined,
      brand: f.brand || undefined,
    };
    getFeedbacks(params)
      .then((data) => {
        setRows(data.results || []);
        setCount(data.count || 0);
      })
      .catch(() => {
        setRows([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRows(1, '', EMPTY_FILTERS, DEFAULT_PAGE_SIZE);
  }, [fetchRows]);

  // Debounce the free-text search so typing doesn't fire a request per keystroke.
  const searchMounted = useRef(false);
  useEffect(() => {
    // Skip the first run: the mount effect above already did this fetch.
    if (!searchMounted.current) {
      searchMounted.current = true;
      return undefined;
    }
    const t = setTimeout(() => {
      setPage(1);
      fetchRows(1, search, filters, pageSize);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const applyFilters = (patch: Partial<Filters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setPage(1);
    fetchRows(1, search, next, pageSize);
  };

  useEffect(() => {
    if (!openId) {
      setOpen(null);
      return;
    }
    if (open?.id === openId) return;
    setReply('');
    // Use the row if it's on this page; otherwise (a link) fetch it.
    const row = rows.find((r) => r.id === openId);
    if (row) {
      setOpen(row);
      return;
    }
    getFeedback(openId)
      .then(setOpen)
      .catch(() => {
        message.error('That feedback could not be found.');
        setSearchParams({}, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const openFeedback = (row: Feedback) => {
    setOpen(row);
    setReply('');
    setSearchParams({ id: String(row.id) });
  };

  const closeFeedback = () => setSearchParams({});

  const sendReply = () => {
    if (!open || !reply.trim()) return;
    setSending(true);
    postFeedbackResponse(open.id, reply.trim())
      .then((updated) => {
        message.success(`Reply sent to ${updated.user.email}.`);
        setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
        setOpen(updated);
        setReply('');
      })
      .catch(() => message.error('Could not send that reply.'))
      .finally(() => setSending(false));
  };

  const columns: ColumnsType<Feedback> = [
    {
      title: noWrapTitle('From'),
      width: 240,
      ellipsis: true,
      render: (_: unknown, row) => (
        <Space size={10} style={{ minWidth: 0 }}>
          <Avatar src={getProfilePhoto(row.user.profile_picture)} size={32} />
          <div style={{ minWidth: 0 }}>
            <CellMain title={row.user.name || undefined}>{row.user.name || 'No name'}</CellMain>
            <CellSub title={row.user.email}>{row.user.email}</CellSub>
          </div>
        </Space>
      ),
    },
    {
      title: noWrapTitle('Feedback'),
      ellipsis: true,
      render: (_: unknown, row) => (
        <div style={{ minWidth: 0 }}>
          <CellMain title={row.title}>{row.title}</CellMain>
          <CellSub>{preview(row.body) || '—'}</CellSub>
        </div>
      ),
    },
    {
      title: noWrapTitle('Brand'),
      width: 100,
      render: (_: unknown, row) =>
        row.user.brand ? (
          <span style={{ textTransform: 'capitalize' }}>{row.user.brand}</span>
        ) : (
          <Muted>—</Muted>
        ),
    },
    {
      title: noWrapTitle('Received'),
      dataIndex: 'created_at',
      width: 132,
      render: (v: string | null) => <DateCell>{fmtDate(v)}</DateCell>,
    },
    {
      title: noWrapTitle('Status'),
      width: 116,
      render: (_: unknown, row) =>
        row.response ? (
          <Pill $tone={TONE_REPLIED}>Replied</Pill>
        ) : (
          <Pill $tone={TONE_UNREPLIED}>Unreplied</Pill>
        ),
    },
  ];

  return (
    <>
      <Toolbar>
        <PageTitle>Feedback</PageTitle>
        <ToolbarActions>
          <Input
            size="small"
            style={{ width: 300 }}
            placeholder="Search name, email, title or message…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
          <Select
            size="small"
            style={{ width: 140 }}
            value={filters.replied}
            onChange={(v) => applyFilters({ replied: v })}
            options={[
              { label: 'Any status', value: '' },
              { label: 'Unreplied', value: 'no' },
              { label: 'Replied', value: 'yes' },
            ]}
          />
          <Select
            size="small"
            style={{ width: 130 }}
            value={filters.brand}
            onChange={(v) => applyFilters({ brand: v })}
            options={[
              { label: 'Any brand', value: '' },
              { label: 'Ease', value: 'ease' },
              { label: 'Wavon', value: 'wavon' },
            ]}
          />
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => fetchRows(page, search, filters, pageSize)}
          >
            Refresh
          </Button>
        </ToolbarActions>
      </Toolbar>

      <ResultCount>
        {count.toLocaleString()} feedback · page {page}
      </ResultCount>

      <CommonCard id="feedback-table" padding="0.5rem 0.75rem 0">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          locale={{ emptyText: <Empty description="No feedback matches these filters" /> }}
          onRow={(row: Feedback) => ({ onClick: () => openFeedback(row), style: { cursor: 'pointer' } })}
          pagination={{
            current: page,
            pageSize,
            total: count,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            // Server-side: changing page or size has to refetch.
            onChange: (p, size) => {
              const nextSize = size || pageSize;
              setPage(p);
              setPageSize(nextSize);
              fetchRows(p, search, filters, nextSize);
            },
          }}
        />
      </CommonCard>

      <Drawer
        title="Feedback"
        placement="right"
        width={560}
        visible={!!openId && !!open}
        onClose={closeFeedback}
      >
        {open && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space size={10}>
              <Avatar src={getProfilePhoto(open.user.profile_picture)} size={40} />
              <div>
                <Text strong>{open.user.name || 'No name'}</Text>
                <div>
                  <Text type="secondary">
                    {open.user.email}
                    {open.user.brand ? ` · ${open.user.brand[0].toUpperCase()}${open.user.brand.slice(1)}` : ''}
                    {open.created_at ? ` · ${fmtDate(open.created_at)}` : ''}
                  </Text>
                </div>
              </div>
            </Space>

            <div
              style={{
                padding: 14,
                background: 'var(--soft-panel-bg)',
                border: 'var(--soft-panel-border)',
                borderRadius: 8,
              }}
            >
              <Title level={5} style={{ marginTop: 0 }}>{open.title}</Title>
              <Text style={{ whiteSpace: 'pre-line' }}>{open.body}</Text>
            </div>

            {open.response ? (
              <div>
                <Text type="secondary">Your reply</Text>
                <div style={{ marginTop: 4 }}>
                  <Text italic style={{ whiteSpace: 'pre-line' }}>{open.response}</Text>
                </div>
              </div>
            ) : (
              <div>
                <TextArea
                  rows={6}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={`Reply to ${open.user.name || open.user.email}. They'll get it by email and a push notification.`}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                  <Button type="primary" onClick={sendReply} loading={sending} disabled={!reply.trim()}>
                    Send reply
                  </Button>
                </div>
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </>
  );
};

export default FeedbackPage;
